
import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';

function LeagueTable({ table, matchStatus }) {
  if (!table || table.length === 0) return (
    <p style={{color:'#4a6a9e',fontSize:'0.74rem',textAlign:'center'}}>No predictions yet — players are joining...</p>
  );
  return (
    <div className="pq-scoreboard" style={{width:'100%'}}>
      <div className="pq-score-hdr">
        {matchStatus === 'finished' ? '🏆 Final Leaderboard' : matchStatus === 'live' ? '📊 Live Standings' : '📋 Predictions In'}
      </div>
      {table.map((row,i) => (
        <div key={row.name} className="pq-score-row" style={{flexDirection:'column',alignItems:'flex-start',gap:3}}>
          <div style={{display:'flex',width:'100%',alignItems:'center',gap:8}}>
            <span className="pq-score-pos">{i===0?'🥇':i===1?'🥈':i===2?'🥉':('#'+(i+1))}</span>
            <span className="pq-score-name" style={{flex:1}}>{row.name}</span>
            <span className="pq-score-pts">{row.pts} pts</span>
          </div>
          {row.prediction && (
            <div style={{paddingLeft:28,fontSize:'0.64rem',color:'#4a6a9e',lineHeight:1.6}}>
              {row.prediction.homeGoals}-{row.prediction.awayGoals}
              {row.prediction.scorers?.length>0 && ` · ⚽ ${row.prediction.scorers.join(', ')}`}
              {row.prediction.corners !== undefined && ` · 🚩 ${row.prediction.corners} corners`}
            </div>
          )}
          {row.breakdown?.length>0 && (
            <div style={{paddingLeft:28,display:'flex',flexWrap:'wrap',gap:4}}>
              {row.breakdown.map((b,j) => (
                <span key={j} style={{fontSize:'0.58rem',color:'#4ade80',background:'rgba(34,197,94,0.1)',
                  borderRadius:4,padding:'1px 6px'}}>+{b.pts} {b.desc}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function LivePanel({ liveData, match }) {
  if (!liveData) return null;
  const goals   = liveData.goals    || [];
  const cards   = liveData.bookings || [];
  const corners = liveData.stats?.CORNER_KICKS;
  const shots   = liveData.stats?.SHOTS_ON_GOAL;

  return (
    <div style={{background:'#0a1628',border:'1px solid #1e3a6e',borderRadius:12,padding:'14px 16px',width:'100%'}}>
      {/* Score */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
        <div style={{textAlign:'center',flex:1}}>
          <div style={{fontSize:'0.72rem',color:'#8ba3cc'}}>{liveData.homeTeam}</div>
          {goals.filter(g=>g.team===liveData.homeTeam).map((g,i)=>(
            <div key={i} style={{fontSize:'0.65rem',color:'#f5c518'}}>⚽ {g.minute}' {g.scorer}{g.type==='OWN_GOAL'?' (OG)':''}</div>
          ))}
        </div>
        <div style={{textAlign:'center',padding:'0 16px'}}>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'2.8rem',color:'#f5c518',lineHeight:1}}>
            {liveData.homeScore??0} – {liveData.awayScore??0}
          </div>
          {liveData.minute && <div style={{fontSize:'0.62rem',color:'#22c55e',animation:'pulse 1.5s ease infinite'}}>⏱ {liveData.minute}'</div>}
        </div>
        <div style={{textAlign:'center',flex:1}}>
          <div style={{fontSize:'0.72rem',color:'#8ba3cc'}}>{liveData.awayTeam}</div>
          {goals.filter(g=>g.team===liveData.awayTeam).map((g,i)=>(
            <div key={i} style={{fontSize:'0.65rem',color:'#f5c518'}}>⚽ {g.minute}' {g.scorer}{g.type==='OWN_GOAL'?' (OG)':''}</div>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
        {corners && (
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:'0.58rem',color:'#4a6a9e',textTransform:'uppercase'}}>Corners</div>
            <div style={{fontSize:'0.84rem',color:'#e8edf8',fontWeight:700}}>{corners.home} – {corners.away}</div>
          </div>
        )}
        {shots && (
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:'0.58rem',color:'#4a6a9e',textTransform:'uppercase'}}>Shots on target</div>
            <div style={{fontSize:'0.84rem',color:'#e8edf8',fontWeight:700}}>{shots.home} – {shots.away}</div>
          </div>
        )}
      </div>

      {/* Cards */}
      {cards.length > 0 && (
        <div style={{marginTop:10,borderTop:'1px solid #1e3a6e',paddingTop:8}}>
          <div style={{fontSize:'0.55rem',letterSpacing:'0.15em',color:'#4a6a9e',textTransform:'uppercase',marginBottom:4}}>Cards</div>
          {cards.map((c,i) => (
            <div key={i} style={{fontSize:'0.67rem',color:'#e8edf8'}}>
              {c.card==='YELLOW_CARD'?'🟨':'🟥'} {c.minute}' {c.player} ({c.team})
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PredictorHost({ match, onBack }) {
  const [phase,setPhase]      = useState('creating'); // creating|lobby|live|finished
  const [code,setCode]        = useState('');
  const [state,setState]      = useState(null);
  const [joiners,setJoiners]  = useState([]);
  const [refreshing,setRefreshing] = useState(false);

  const onMsg = useCallback((msg) => {
    switch(msg.type) {
      case 'PREDICTOR_CREATED':
        setCode(msg.code); setPhase('lobby'); break;
      case 'PREDICTOR_STATE':
        setState(msg);
        setPhase(msg.status==='finished'?'finished':msg.status==='live'?'live':'lobby');
        break;
      case 'PREDICTOR_PLAYER_JOINED':
        setJoiners(j => [...j, msg.name]); break;
    }
  }, []);

  const { send } = useWebSocket(onMsg);

  useEffect(() => {
    if (match) send('PREDICTOR_CREATE', { matchId: match.id });
  }, [send, match]);

  // Poll for live updates every 60s
  useEffect(() => {
    if (phase !== 'live') return;
    const t = setInterval(() => {
      setRefreshing(true);
      send('PREDICTOR_FETCH_LIVE');
      setTimeout(() => setRefreshing(false), 1500);
    }, 60000);
    return () => clearInterval(t);
  }, [phase, send]);

  const manualRefresh = () => {
    setRefreshing(true);
    send('PREDICTOR_FETCH_LIVE');
    setTimeout(() => setRefreshing(false), 2000);
  };

  const table    = state?.table || [];
  const liveData = state?.liveData;
  const predCount= Object.keys(state?.predictions||{}).length;

  return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr">
        <button className="g-back" onClick={onBack}>← Menu</button>
        <div className="g-title" style={{color:'#38bdf8'}}>PREDICTOR HOST</div>
        <div className="g-pills">
          <div className="g-pill b"><span className="g-plbl">Players</span><span className="g-pval">{predCount}</span></div>
          {code&&<div className="g-pill b"><span className="g-plbl">Code</span><span className="g-pval">{code}</span></div>}
        </div>
      </div>

      {phase==='creating'&&(
        <div className="game-home"><div className="phone-waiting">
          <div className="phone-icon">⚙️</div>
          <p className="phone-status">Creating session...</p>
        </div></div>
      )}

      {(phase==='lobby'||phase==='live'||phase==='finished')&&(
        <div className="game-body" style={{maxWidth:680}}>

          {/* Match header */}
          <div style={{background:'#0f1e38',border:'1px solid #1e3a6e',borderRadius:12,padding:'14px 16px',textAlign:'center'}}>
            <div style={{fontSize:'0.55rem',letterSpacing:'0.18em',color:'#8ba3cc',textTransform:'uppercase',marginBottom:6}}>{match.competition}</div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:16}}>
              <span style={{fontSize:'1rem',fontWeight:700,color:'#e8edf8'}}>{match.homeShort}</span>
              <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.4rem',color:'#3a5a8e'}}>VS</span>
              <span style={{fontSize:'1rem',fontWeight:700,color:'#e8edf8'}}>{match.awayShort}</span>
            </div>
            <div style={{fontSize:'0.65rem',color:'#4a6a9e',marginTop:4}}>
              {new Date(match.utcDate).toLocaleString([],{weekday:'short',hour:'2-digit',minute:'2-digit'})}
            </div>
          </div>

          {/* Join code */}
          <div style={{background:'#0a1628',border:'2px solid #0369a1',borderRadius:12,padding:'14px 16px',textAlign:'center'}}>
            <div style={{fontSize:'0.55rem',letterSpacing:'0.2em',color:'#4a6a9e',textTransform:'uppercase'}}>Session Code — Players join with this code</div>
            <div className="lobby-code" style={{fontSize:'3.2rem'}}>{code}</div>
            <div style={{fontSize:'0.64rem',color:'#4a6a9e'}}>
              Session stays alive for 24h · Players can leave and rejoin at any time
            </div>
          </div>

          {/* Live data */}
          {liveData && <LivePanel liveData={liveData} match={match} />}

          {/* Manual refresh for live */}
          {phase==='live'&&(
            <button onClick={manualRefresh} style={{background:'transparent',border:'1px solid #1e3a6e',
              borderRadius:8,color:refreshing?'#22c55e':'#8ba3cc',padding:'6px 14px',cursor:'pointer',
              fontSize:'0.74rem',fontFamily:'Inter,sans-serif',transition:'color 0.2s'}}>
              {refreshing?'Refreshing...':'↻ Refresh Live Data'}
            </button>
          )}

          {/* Predictions in */}
          {phase==='lobby'&&predCount===0&&(
            <div style={{textAlign:'center',padding:16}}>
              <p style={{fontSize:'0.8rem',color:'#8ba3cc'}}>Waiting for players to submit predictions...</p>
              <p style={{fontSize:'0.68rem',color:'#4a6a9e',marginTop:4}}>Players join at <strong style={{color:'#38bdf8'}}>games.housemonk.co.uk</strong> → Predictor → Join with code <strong style={{color:'#38bdf8'}}>{code}</strong></p>
            </div>
          )}

          {/* League table */}
          <LeagueTable table={table} matchStatus={state?.status} />

          {/* Recent joiners */}
          {joiners.length>0&&phase==='lobby'&&(
            <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
              <span style={{fontSize:'0.62rem',color:'#4a6a9e'}}>Joined:</span>
              {joiners.slice(-8).map((n,i)=>(
                <span key={i} style={{fontSize:'0.64rem',color:'#4ade80',background:'rgba(34,197,94,0.1)',
                  borderRadius:4,padding:'1px 7px'}}>{n}</span>
              ))}
            </div>
          )}

          {phase==='finished'&&(
            <div style={{textAlign:'center',padding:12}}>
              <p style={{fontSize:'0.72rem',color:'#8ba3cc'}}>Match finished · Session remains open for 24h</p>
              <p style={{fontSize:'0.64rem',color:'#4a6a9e',marginTop:4}}>Players can still rejoin with code <strong style={{color:'#38bdf8'}}>{code}</strong> to see final standings</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
